import { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import CrmModal from '../components/CrmModal';
import { IconUsers } from '../components/SvgIcons';

/**
 * AddEditClientModal v2
 * Only captures permanent client identity information.
 * Service orders are managed separately via ServiceOrderModal inside ClientProfile.
 */
export default function AddEditClientModal({ client, currency, onClose, onSaved }) {
  const isEditing = Boolean(client);

  const [referenceSources, setReferenceSources] = useState([]);
  const [form, setForm] = useState({
    clientName: client?.clientName || '',
    company: client?.company || '',
    mobile: client?.mobile || '',
    email: client?.email || '',
    address: client?.address || '',
    notes: client?.notes || '',
    joinDate: client?.joinDate || new Date().toISOString().split('T')[0],
    status: client?.status || 'Active',
    referenceSource: client?.referenceSource || 'Direct Inquiry',
  });

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get('/api/client-module/references')
      .then((data) => setReferenceSources(data || []))
      .catch(() => {});
  }, []);

  function set(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!form.clientName.trim()) { setError('Client Name is required'); return; }
    setBusy(true);
    setError(null);
    try {
      const payload = {
        clientName: form.clientName.trim(),
        company: (form.company || form.clientName).trim(),
        mobile: form.mobile.trim(),
        email: form.email.trim(),
        address: form.address.trim(),
        notes: form.notes.trim(),
        joinDate: form.joinDate,
        status: form.status,
        referenceSource: form.referenceSource,
      };

      if (isEditing) {
        await api.put(`/api/client-module/clients/${client.id}`, payload);
      } else {
        await api.post('/api/client-module/clients', payload);
      }
      onSaved();
    } catch (err) {
      setError(err.message || 'Failed to save client');
    } finally {
      setBusy(false);
    }
  };

  return (
    <CrmModal
      onClose={onClose}
      title={isEditing ? `Edit Client — ${client.clientName}` : 'Add New Client'}
      subtitle={isEditing ? 'Update basic client information. Use Service Orders to add new services.' : 'Create a permanent client profile. Add service orders after creation.'}
      icon={IconUsers}
      maxWidth={640}
      footer={
        <>
          <button type="button" className="cm-btn-outline" onClick={onClose}>Cancel</button>
          <button type="button" className="cm-btn-emerald" onClick={handleSubmit} disabled={busy}>
            {busy ? 'Saving…' : isEditing ? 'Save Changes' : 'Create Client'}
          </button>
        </>
      }
    >
      <form onSubmit={handleSubmit}>
        {error && <div className="err" style={{ marginBottom: 14, fontWeight: 700 }}>{error}</div>}

        {/* Basic Info */}
        <div className="cm-form-section-card" style={{ marginBottom: 16 }}>
          <div className="cm-form-section-title">Client Identity</div>
          <div className="cm-grid-2">
            <div className="cm-field-group">
              <label>Client / Person Name *</label>
              <input
                autoFocus
                required
                value={form.clientName}
                onChange={(e) => set('clientName', e.target.value)}
                placeholder="e.g. Rahul Shah"
              />
            </div>
            <div className="cm-field-group">
              <label>Company / Business Name</label>
              <input
                value={form.company}
                onChange={(e) => set('company', e.target.value)}
                placeholder="e.g. ABC Fashion Pvt. Ltd."
              />
            </div>
          </div>
          <div className="cm-grid-2">
            <div className="cm-field-group">
              <label>Mobile Number</label>
              <input
                type="tel"
                value={form.mobile}
                onChange={(e) => set('mobile', e.target.value)}
                placeholder="+91 98765 43210"
              />
            </div>
            <div className="cm-field-group">
              <label>Email Address</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => set('email', e.target.value)}
                placeholder="client@email.com"
              />
            </div>
          </div>
          <div className="cm-field-group">
            <label>Address / City</label>
            <input
              value={form.address}
              onChange={(e) => set('address', e.target.value)}
              placeholder="Ahmedabad, Gujarat"
            />
          </div>
        </div>

        {/* Onboarding */}
        <div className="cm-form-section-card" style={{ marginBottom: 16 }}>
          <div className="cm-form-section-title">Onboarding Details</div>
          <div className="cm-grid-2">
            <div className="cm-field-group">
              <label>Join Date</label>
              <input type="date" value={form.joinDate} onChange={(e) => set('joinDate', e.target.value)} />
            </div>
            <div className="cm-field-group">
              <label>Client Status</label>
              <select value={form.status} onChange={(e) => set('status', e.target.value)}>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
                <option value="Lead">Lead</option>
                <option value="Lost">Lost</option>
              </select>
            </div>
          </div>
          <div className="cm-field-group">
            <label>Reference / Lead Source</label>
            <select value={form.referenceSource} onChange={(e) => set('referenceSource', e.target.value)}>
              {referenceSources.map((r) => (
                <option key={r.id} value={r.sourceName}>{r.sourceName}</option>
              ))}
              <option value="Direct Inquiry">Direct Inquiry</option>
              <option value="Other">Other</option>
            </select>
          </div>
        </div>

        {/* Notes */}
        <div className="cm-form-section-card" style={{ marginBottom: 0 }}>
          <div className="cm-form-section-title">Notes (Optional)</div>
          <div className="cm-field-group">
            <textarea
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              rows={3}
              placeholder="Any internal notes about this client…"
            />
          </div>
        </div>

        {!isEditing && (
          <div style={{ background: 'var(--crm-emerald-light)', border: '1px solid var(--crm-border)', borderRadius: 10, padding: '12px 16px', marginTop: 14, fontSize: '0.82rem', color: 'var(--crm-emerald)', fontWeight: 600 }}>
            💡 After creating the client, open their profile and click "+ Add New Service Order" to add services, pricing, and dates.
          </div>
        )}
      </form>
    </CrmModal>
  );
}
